import React from 'react';
import { Toaster as ShadToaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { usePageTracking } from "./hooks/usePageTracking";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { HelmetProvider } from "react-helmet-async";
import UTMTracker from "@/components/UTMTracker";
import AuthRedirect from "./components/auth/AuthRedirect";
import Index from "./pages/Index";
import ClientAuth from "./pages/ClientAuth";
import PractitionerAuth from "./pages/PractitionerAuth";
import BecomePractitioner from "./pages/BecomePractitioner";
import Practitioners from "./pages/Practitioners";

import BookingDatetime from "./pages/BookingDateTime";
import Payment from "./pages/Payment";
import BookingSuccess from "./pages/BookingSuccess";
import PractitionerDashboard from "./pages/PractitionerDashboard";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminRoutes from "./routes/adminRoutes";
import PaymentCancelled from "./pages/PaymentCancelled";
import AboutUs from "./pages/AboutUs";
import ClientDashboard from "./pages/ClientDashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import UpdatePassword from "./pages/UpdatePassword";
import NewsEvents from "./pages/NewsEvents";
import NewsArticle from "./pages/NewsArticle";
import CertificationsList from "./pages/CertificationsList";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import ZoomOAuthCallback from "./pages/ZoomOAuthCallback";
import GoogleCalendarOAuthCallback from "./pages/GoogleCalendarOAuthCallback";
import PractitionerDetail from "./components/practitioners/PractitionerDetail";
import { SessionFunctionPage } from "./components/session";
import SessionToolsPage from "./pages/SessionToolsPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import CareersPage from "./pages/CareersPage";
import AccessibilityPage from "./pages/AccessibilityPage";
import BookingChoice from "./pages/BookingChoice";
import CrisisResources from "./pages/CrisisResources";
import ServiceSelection from "./pages/ServiceSelection";

// i18n instance for provider
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/config';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppWithProviders() {
  console.log('AppWithProviders rendering, React version:', React.version);
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <div className="min-h-screen w-full">
                <div className="min-h-screen pb-safe">
                  <BrowserRouter>
                    <ErrorBoundary>
                      {mounted && <ShadToaster />}
                      <AppRoutes />
                    </ErrorBoundary>
                  </BrowserRouter>
                </div>
              </div>
            </AuthProvider>
          </QueryClientProvider>
        </I18nextProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  // Page tracking must be called unconditionally
  usePageTracking();

  return (
    <>
      <UTMTracker />
      <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/client-auth" element={<ClientAuth />} />
      <Route path="/practitioner-auth" element={<PractitionerAuth />} />
      <Route path="/become-practitioner" element={<BecomePractitioner />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/update-password" element={<UpdatePassword />} />
      <Route path="/booking-choice" element={<BookingChoice />} />
      <Route path="/crisis-resources" element={<CrisisResources />} />
      <Route path="/service-selection" element={<ServiceSelection />} />
      <Route path="/book-an-appointment" element={<Practitioners />} />
      <Route path="/book-an-appointment/:id" element={<PractitionerDetail isFullPage={true} />} />
      <Route path="/certifications" element={<CertificationsList />} />
      
      <Route path="/booking-datetime" element={<BookingDatetime />} />
      <Route path="/payment" element={<Payment />} />
      <Route path="/booking-success" element={<BookingSuccess />} />
      <Route path="/practitioner-dashboard" element={<ProtectedRoute requiredRole={"practitioner"} element={<PractitionerDashboard />} />} />
      <Route path="/client-dashboard" element={<ProtectedRoute requiredRole={"client"} element={<ClientDashboard />} />} />
      
      <Route path="/session/:bookingId" element={<SessionFunctionPage />} />
      <Route path="/session-tools/:bookingId" element={<ProtectedRoute requiredRole={"practitioner"} element={<SessionToolsPage />} />} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/*" element={<ProtectedRoute requiredRole={"admin"} element={<AdminRoutes />} />} />
      <Route path="/payment-cancelled" element={<PaymentCancelled />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/zoom/callback" element={<ZoomOAuthCallback />} />
      <Route path="/oauth/google-calendar/callback" element={<GoogleCalendarOAuthCallback />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/accessibility" element={<AccessibilityPage />} />
      <Route path="/news-events" element={<NewsEvents />} />
      <Route path="/news/:slug" element={<NewsArticle />} />
      <Route path="/dashboard" element={<AuthRedirect />} />
      <Route path="/profile" element={<AuthRedirect />} />
      </Routes>
    </>
  );
}

export default AppWithProviders;